import { Component, inject, signal } from '@angular/core';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { FeeService } from '../../../../../services/fees';
import { FlashService } from '../../../../../services/flash';
import { ISchools } from '../../../../../interfaces/school';
import { IFeeStructure, IFeeStructures, IFeeSummary } from '../../../../../interfaces/fees';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { FormsModule, NgForm } from '@angular/forms';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { DecimalPipe, NgClass } from '@angular/common';
import { Helpers } from '../../../../../utilities/helpers';
import { ILevels } from '../../../../../interfaces/level';
import { LevelService } from '../../../../../services/level';
import { IStudent, IStudentEnrollments, IStudents } from '../../../../../interfaces/student';
import { StudentService } from '../../../../../services/student';
import { IAcademicYears } from '../../../../../interfaces/academic-year';
import { AcademicYearService } from '../../../../../services/academic-year';

@Component({
  selector: 'app-fee-structure',
  imports: [
    ActionButton,
    FormsModule,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    NgClass,
  ],
  templateUrl: './fee-structure.html',
  styleUrl: './fee-structure.css',
})
export class FeeStructure {
  constructor(
    private session: Session,
    private schoolService: SchoolService,
    private feeService: FeeService,
    private levelService: LevelService,
    private studentService: StudentService,
    protected academicYearService: AcademicYearService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getFees();
    this.getLevels();
    this.getAcademicYears();
  }

  protected schoolId?: string;
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schools = signal<ISchools | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected fees = signal<IFeeStructures | null>(null);
  protected filterSchoolId: string = '';
  protected searchFees: string = '';
  protected feeStatus: string = '';
  protected readonly Helpers = Helpers;
  protected feeStructureStatuses = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];
  protected levels = signal<ILevels | null>(null);
  protected dashboardData = signal<any>(null);
  protected setChangeStatusPopup = signal<boolean>(false);
  protected setAssignFee = signal<boolean>(false);
  protected setEditFee = signal<boolean>(false);
  protected editFeeName: string = '';
  protected editSelectedLevels: string[] = [];
  protected newStatus: string = '';
  protected selectedFee!: IFeeStructure['payload'] | undefined;
  protected isEditing = signal<boolean>(false);
  protected query: string = '';
  protected students = signal<IStudents | null>(null);
  protected selectedStudent: IStudent['payload'] | undefined;
  protected studentEnrollment = signal<IStudentEnrollments | null>(null);
  protected selectedFeeStructure = signal<IFeeStructures | null>(null);
  protected discountAmount!: number;
  protected adjustmentAmount!: number;
  protected adjustmentReason: string = '';
  protected academicYears = signal<IAcademicYears | null>(null);
  protected academicYearId: string = '';
  protected feeRequired: boolean = true;
  protected setFeeDetail = signal(false);
  protected feeSummary = signal<IFeeSummary | null>(null);

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected selectStudent(student: IStudent['payload']) {
    this.query = '';
    this.selectedStudent = student;
    this.getStudentEnrollment(student.id);
  }

  protected getLevels(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
    };

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (res) => {
        this.levels.set(res);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Refresh the page');
      },
    });
  }

  protected getFees(filterType = this.currentFilterType, value = this.currentFilterValue): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.feeService.getFeeStructures(`${ApiEndpoints.url}/fee-structure`, params).subscribe({
      next: (res) => {
        this.fees.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load fees. Refresh the page');
      },
    });
  }

  protected findFee(levelId: string, name: string, academicYear: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 5,
      level_id: levelId,
      search: name,
      academic_year_id: academicYear,
    };

    this.feeService.getFeeStructures(`${ApiEndpoints.url}/fee-structure`, params).subscribe({
      next: (res) => {
        if (!res.payload.length) {
          this.flashService.failure('This fee is not assignable to this student');
          this.setAssignFee.set(false);
          this.selectedFee = undefined;

          return;
        }

        const feeStructureId = res.payload[0].fee_structure_ids[0];

        if (!feeStructureId) {
          this.flashService.failure('This fee is not assignable to this student');
          this.setAssignFee.set(false);
          this.selectedFee = undefined;
          return;
        }

        this.selectedFeeStructure.set(res);

        const studentFeeParams: Record<string, string | number> = {
          page: 0,
          size: 1,
          student_id: this.selectedStudent?.id ?? '',
          fee_structure_id: feeStructureId,
        };

        this.feeService
          .getStudentFees(`${ApiEndpoints.url}/student-fee`, studentFeeParams)
          .subscribe({
            next: (studentFeeRes) => {
              if (studentFeeRes.payload.length) {
                this.flashService.failure('This fee has already been assigned to this student');
                this.setAssignFee.set(false);
                this.selectedFee = undefined;
                this.selectedStudent = undefined;
                return;
              }
            },
            error: (err) => {
              this.flashService.failure(
                err?.error?.message ?? 'Could not verify fee assignment. Refresh the page',
              );
            },
          });
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load fees. Refresh the page');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getFees();
  }

  protected selectFee(fee: IFeeStructure['payload'], action: string): void {
    this.selectedStudent = undefined;
    this.selectedFee = fee;

    if (action === 'edit') {
      this.setChangeStatusPopup.set(true);
    } else if (action === 'assign') {
      this.setAssignFee.set(true);
    } else if (action === 'edit-fee') {
      this.editFeeName = fee.fee_name;
      this.editSelectedLevels = fee.levels.map((level) => level.id);
      this.setEditFee.set(true);
    } else {
      this.getFeeSummary(fee.term_id, fee.fee_name)
      this.setFeeDetail.set(true);
    }
  }

  protected selectEditLevel(levelId: string): void {
    if (this.editSelectedLevels.includes(levelId)) {
      this.editSelectedLevels = this.editSelectedLevels.filter((id) => id !== levelId);

      return;
    }

    this.editSelectedLevels = [...this.editSelectedLevels, levelId];
  }

  protected get areAllEditLevelsSelected(): boolean {
    const allLevels = this.levels()?.payload ?? [];

    return allLevels.length > 0 && allLevels.every((level) => this.editSelectedLevels.includes(level.id));
  }

  protected selectAllEditLevels(): void {
    const allLevels = this.levels()?.payload ?? [];

    if (this.areAllEditLevelsSelected) {
      this.editSelectedLevels = [];
    } else {
      this.editSelectedLevels = allLevels.map((level) => level.id);
    }
  }

  protected updateFee(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.editSelectedLevels.length) {
      this.flashService.failure('Select at least one level');
      return;
    }

    this.isEditing.set(true);

    const editFeeForm = {
      fee_structure_ids: this.selectedFee?.fee_structure_ids ?? [],
      level_ids: this.editSelectedLevels,
      fee_name: this.editFeeName,
    };

    this.feeService.edit(`${ApiEndpoints.url}/fee-structure/edit`, editFeeForm).subscribe({
      next: () => {
        this.flashService.success('Fee updated successfully.');
        this.isEditing.set(false);
        this.setEditFee.set(false);
        this.getFees();
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error updating fee. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected getStudents(value: string): void {
    const params = {
      page: 0,
      size: 50,
      status: 'ACTIVE',
    };

    this.studentService
      .getStudents(`${ApiEndpoints.url}/student?search=${value}`, params)
      .subscribe({
        next: (data) => {
          this.students.set(data);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Error loading students.');
        },
      });
  }

  protected getStudentEnrollment(studentId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 1,
      student_id: studentId,
    };

    this.studentService
      .getStudentEnrollments(`${ApiEndpoints.url}/student-enrollment`, params)
      .subscribe({
        next: (data) => {
          if (!data.payload.length) {
            this.flashService.failure('Assign student to a class first');
            this.setAssignFee.set(false);
            return;
          }

          const studentLevelId = data.payload[0].level_id;

          const levelMatches = (this.selectedFee?.levels ?? []).some(
            (level) => level.id === studentLevelId,
          );

          if (!levelMatches) {
            this.flashService.failure('This fee is not assignable to this student level');
            this.setAssignFee.set(false);
            this.selectedFee = undefined;
            return;
          }

          this.findFee(
            studentLevelId,
            this.selectedFee?.fee_name ?? '',
            data.payload[0].academic_year_id,
          );
          this.studentEnrollment.set(data);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load students. Refresh the page',
          );
        },
      });
  }

  protected assignFee(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    if (this.adjustmentAmount && !this.adjustmentReason) {
      this.flashService.failure('Must provide a reason for adjustment');
      return;
    }

    let discount = 0;

    if (this.discountAmount) {
      discount = (this.discountAmount / 100) * Number(this.selectedFee?.amount ?? 0);
    }

    const assignFeeForm = {
      student_id: this.selectedStudent?.id ?? '',
      enrollment_id: this.studentEnrollment()?.payload?.[0]?.id,
      fee_structure_id: this.selectedFeeStructure()?.payload?.[0]?.fee_structure_ids?.[0],
      discount_amount: discount,
      adjustment_amount: this.adjustmentAmount,
      adjustment_reason: this.adjustmentReason,
    };

    this.feeService.create(`${ApiEndpoints.url}/student-fee/assign`, assignFeeForm).subscribe({
      next: () => {
        this.flashService.success('Fee assigned successfully.');
        this.isEditing.set(false);
        this.setAssignFee.set(false);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error recording payment. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected changeFeeStatus(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const payload = {
      fee_structure_ids: this.selectedFee?.fee_structure_ids ?? [],
      status: this.newStatus,
      required: this.feeRequired,
    };

    this.feeService.edit(`${ApiEndpoints.url}/fee-structure/change-status`, payload).subscribe({
      next: () => {
        this.flashService.success('Fee status updated successfully.');
        this.isEditing.set(false);
        this.setChangeStatusPopup.set(false);
        this.getFees();
        this.newStatus = '';
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error updating fee status. Try again.');
        this.isEditing.set(false);
      },
    });
  }

  protected getFeeSummary(term: string, name: string): void {
    const params = {
      term_id: term,
      fee_name: name
    };

    this.feeService.getFeeSummary(`${ApiEndpoints.url}/fee-structure/summary`, params).subscribe({
      next: (res) => {
        this.feeSummary.set(res);
      }, error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading fee summary');
      }
    })
  }
}
